export const TAILGATE_SIDECAR_SCHEMA_VERSION = '1.0.0'
const TAILGATE_SIDECAR_SCHEMA_MAJOR_VERSION = 1
const SEMVER_PATTERN =
  /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-[0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*)?(?:\+[0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*)?$/

export type TailgateSidecarScalingMode =
  | 'stored_resolution'
  | 'decoded_fallback'
  | 'none'

export interface TailgateSidecarTrackI {
  box: [number, number, number, number]
  id: number
  in_secured: boolean
  in_unsecured: boolean
}

export interface TailgateGeometrySidecarI {
  schema_version: string
  evaluator_version: string
  model_version: string
  grid_hash: string
  scaling_mode: TailgateSidecarScalingMode
  resolution_used: [number, number] | null
  frame: { h: number; w: number }
  fps: number
  frame_count: number
  zones: {
    secured: Array<[number, number]>
    unsecured: Array<[number, number]>
  }
  verdict: {
    badge_count: number
    person_count: number
    type: string
  }
  crossings: Array<{ frame: number; track_id: number }>
  frames: TailgateSidecarTrackI[][]
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value)

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0

const getSemverMajorVersion = (value: unknown): number | null => {
  if (!isNonEmptyString(value)) return null

  const match = SEMVER_PATTERN.exec(value)
  return match ? Number(match[1]) : null
}

const isPoint = (value: unknown): value is [number, number] =>
  Array.isArray(value) && value.length === 2 && value.every(isFiniteNumber)

const isTrack = (value: unknown): value is TailgateSidecarTrackI =>
  isRecord(value) &&
  Number.isInteger(value.id) &&
  Array.isArray(value.box) &&
  value.box.length === 4 &&
  value.box.every(isFiniteNumber) &&
  typeof value.in_unsecured === 'boolean' &&
  typeof value.in_secured === 'boolean'

const isCrossing = (
  value: unknown
): value is TailgateGeometrySidecarI['crossings'][number] =>
  isRecord(value) &&
  Number.isInteger(value.frame) &&
  Number(value.frame) >= 0 &&
  Number.isInteger(value.track_id)

export const isTailgateGeometrySidecar = (
  value: unknown
): value is TailgateGeometrySidecarI => {
  if (!isRecord(value)) return false
  if (
    getSemverMajorVersion(value.schema_version) !==
    TAILGATE_SIDECAR_SCHEMA_MAJOR_VERSION
  ) {
    return false
  }
  if (!isNonEmptyString(value.evaluator_version)) return false
  if (!isNonEmptyString(value.model_version)) return false
  if (!isNonEmptyString(value.grid_hash)) return false
  if (!isRecord(value.frame)) return false
  if (!isFiniteNumber(value.frame.w) || value.frame.w <= 0) return false
  if (!isFiniteNumber(value.frame.h) || value.frame.h <= 0) return false
  if (!isFiniteNumber(value.fps) || value.fps <= 0) return false
  if (!Number.isInteger(value.frame_count) || Number(value.frame_count) < 0) {
    return false
  }
  if (!Array.isArray(value.frames)) return false
  if (value.frames.length !== value.frame_count) return false
  if (
    !value.frames.every((frame) => Array.isArray(frame) && frame.every(isTrack))
  ) {
    return false
  }
  if (!isRecord(value.zones)) return false
  if (
    !Array.isArray(value.zones.unsecured) ||
    !value.zones.unsecured.every(isPoint) ||
    !Array.isArray(value.zones.secured) ||
    !value.zones.secured.every(isPoint)
  ) {
    return false
  }
  if (
    value.scaling_mode !== 'stored_resolution' &&
    value.scaling_mode !== 'decoded_fallback' &&
    value.scaling_mode !== 'none'
  ) {
    return false
  }
  if (
    value.resolution_used !== null &&
    (!Array.isArray(value.resolution_used) ||
      value.resolution_used.length !== 2 ||
      !value.resolution_used.every(
        (dimension) => isFiniteNumber(dimension) && dimension > 0
      ))
  ) {
    return false
  }
  if (!isRecord(value.verdict)) return false
  if (
    (value.verdict.type !== 'TL' && value.verdict.type !== 'NTL') ||
    !Number.isInteger(value.verdict.person_count) ||
    Number(value.verdict.person_count) < 0 ||
    !Number.isInteger(value.verdict.badge_count) ||
    Number(value.verdict.badge_count) < 0
  ) {
    return false
  }
  if (
    !Array.isArray(value.crossings) ||
    !value.crossings.every(
      (crossing) =>
        isCrossing(crossing) && crossing.frame < Number(value.frame_count)
    )
  ) {
    return false
  }

  return true
}
